import {
    createStatementRuleIntegrationAdapter,
} from "../executableFamilies/statementRule/StatementRuleIntegrationAdapter";
import type { EnrichedPart } from "../../domain/types";
import type { RuleOutcomeKey, RuleOutcomeRouting } from "../../rules/ruleLogicDefinitions";
import type {
    AnyDemoRuleIntegrationAdapter,
    RuleIntegrationRegistry,
    RuleIntegrationRuntimeSamples,
} from "../ruleIntegrationTypes";
import {
    DQ001_RULE_ID,
    DQ001_RUNTIME_SAMPLES,
    DQ001_STATEMENT_RULE_POLICY,
} from "./statementRules/dq001MandatoryAttributesPolicy";
import {
    DQ002_RULE_ID,
    DQ002_RUNTIME_SAMPLES,
    DQ002_STATEMENT_RULE_POLICY,
} from "./statementRules/dq002UnitOfMeasurePolicy";
import {
    DQ003_RULE_ID,
    DQ003_RUNTIME_SAMPLES,
    DQ003_STATEMENT_RULE_POLICY,
} from "./statementRules/dq003LifecycleStatusPolicy";
import {
    DQ004_RULE_ID,
    DQ004_RUNTIME_SAMPLES,
    DQ004_STATEMENT_RULE_POLICY,
} from "./statementRules/dq004MpnFormatPolicy";
import {
    createNumericThresholdStatementRulePolicy,
} from "./statementRules/numericThresholdStatementPolicy";
import type { AnyStatementRuleIntegrationPolicy } from "./statementRulePolicyTypes";

const CF013_RULE_ID = "CF-013";

const CF013_DEFAULT_OUTCOME_ROUTING: Record<RuleOutcomeKey, RuleOutcomeRouting> = {
    pass: {
        id: "cf013-pass-routing",
        when: "pass",
        status: "pass",
        label: "publish candidate",
        outputQueue: "published_catalog_candidates",
        reasonCode: "CONFIDENCE_ACCEPTED",
        explanation: "Enrichment confidence meets the publish threshold.",
    },
    fail: {
        id: "cf013-fail-routing",
        when: "fail",
        status: "flagged",
        label: "route low-confidence rows to pending review",
        outputQueue: "pending_review",
        reasonCode: "LOW_CONFIDENCE",
        explanation: "Enrichment confidence is below threshold, so downstream publish is paused.",
    },
    error: {
        id: "cf013-error-routing",
        when: "error",
        status: "rejected",
        label: "hold incomplete expression",
        outputQueue: "pending_review",
        reasonCode: "APPGLUE_EXPRESSION_INCOMPLETE",
        explanation:
            "The AppGlue expression is incomplete, so validation preview is holding rows until the expression is completed.",
    },
};

const samplePart = (overrides: Partial<EnrichedPart> = {}): EnrichedPart => ({
    partId: "PART-CF013-SAMPLE",
    mpn: "TI-0001SEM",
    manufacturerId: "MFR-001",
    manufacturerName: "Texas Instruments",
    category: "Semiconductor",
    taxonomyPath: "Semiconductor > Logic",
    packageType: "QFN",
    pinCount: 32,
    voltageMin: 1.8,
    voltageMax: 3.3,
    capacitancePf: null,
    resistanceOhms: null,
    tolerancePct: 5,
    rohsStatus: "Compliant",
    reachStatus: "Compliant",
    lifecycleStatus: "Active",
    priorLifecycleStatus: "Active",
    confidenceScore: 95,
    source: "manufacturer_direct",
    recommendedReplacementMpn: null,
    hasMandatoryAttributeGap: false,
    hasUomConflict: false,
    hasMpnFormatIssue: false,
    hasComplianceChange: false,
    hasSourceConflict: false,
    ...overrides,
});

const CF013_RUNTIME_SAMPLES = {
    passingRow: samplePart(),
    routedRow: samplePart({
        partId: "PART-CF013-ROUTED",
        confidenceScore: 72,
    }),
};

const CF013_STATEMENT_RULE_POLICY = createNumericThresholdStatementRulePolicy<EnrichedPart>({
    ruleId: CF013_RULE_ID,
    title: "CF-013 Enrichment confidence threshold",
    conditionId: "cf013-confidence-threshold",
    defaultField: "confidence_score",
    defaultOperator: ">=",
    defaultThreshold: 85,
    fieldOptions: [
        { value: "confidence_score", label: "confidence_score" },
        { value: "pin_count", label: "pin_count" },
    ],
    valueInputLabel: "confidence threshold",
    comparisonLabel: "Confidence",
    summaryActionWhen: "fail",
    defaultRouting: CF013_DEFAULT_OUTCOME_ROUTING,
    getNumericValue: (part, field) => {
        if (field === "confidence_score") return part.confidenceScore;
        if (field === "pin_count") return part.pinCount;
        return null;
    },
    formatComparisonValue: (value) => `${value.toFixed(2)}%`,
    runtimeSamples: CF013_RUNTIME_SAMPLES,
});

export {
    CF013_RULE_ID,
    CF013_RUNTIME_SAMPLES,
    DQ001_RULE_ID,
    DQ001_RUNTIME_SAMPLES,
    DQ002_RULE_ID,
    DQ002_RUNTIME_SAMPLES,
    DQ003_RULE_ID,
    DQ003_RUNTIME_SAMPLES,
    DQ004_RULE_ID,
    DQ004_RUNTIME_SAMPLES,
};

export const STATEMENT_RULE_POLICY_REGISTRY: Record<string, AnyStatementRuleIntegrationPolicy> = {
    [DQ001_RULE_ID]: DQ001_STATEMENT_RULE_POLICY,
    [DQ002_RULE_ID]: DQ002_STATEMENT_RULE_POLICY,
    [DQ003_RULE_ID]: DQ003_STATEMENT_RULE_POLICY,
    [DQ004_RULE_ID]: DQ004_STATEMENT_RULE_POLICY,
    [CF013_RULE_ID]: CF013_STATEMENT_RULE_POLICY,
};

export const STATEMENT_RULE_RUNTIME_SAMPLES: RuleIntegrationRuntimeSamples = {
    [DQ001_RULE_ID]: DQ001_RUNTIME_SAMPLES,
    [DQ002_RULE_ID]: DQ002_RUNTIME_SAMPLES,
    [DQ003_RULE_ID]: DQ003_RUNTIME_SAMPLES,
    [DQ004_RULE_ID]: DQ004_RUNTIME_SAMPLES,
    [CF013_RULE_ID]: CF013_RUNTIME_SAMPLES,
};

const createAdapterFromStatementRulePolicy = (
    policy: AnyStatementRuleIntegrationPolicy,
): AnyDemoRuleIntegrationAdapter =>
    createStatementRuleIntegrationAdapter({
        ruleId: policy.ruleId,
        policyBinding: policy.policyBinding,
        createConfig: policy.createConfig,
        rowMapper: policy.rowMapper,
        evaluator: policy.evaluator,
        summaryPresenter: policy.summaryPresenter,
        simplifiedLogicEditor: policy.simplifiedLogicEditor,
        demoDataProjection: policy.demoDataProjection,
        suggestionCommands: policy.suggestionCommands,
    });

export const STATEMENT_RULE_ADAPTER_REGISTRY: RuleIntegrationRegistry = Object.fromEntries(
    Object.values(STATEMENT_RULE_POLICY_REGISTRY).map((policy) => [
        policy.ruleId,
        createAdapterFromStatementRulePolicy(policy),
    ]),
);

export const getStatementRuleIntegrationAdapter = (ruleId: string): AnyDemoRuleIntegrationAdapter | undefined =>
    STATEMENT_RULE_ADAPTER_REGISTRY[ruleId];

export const getRegisteredStatementRuleIntegrationAdapters = (): RuleIntegrationRegistry =>
    STATEMENT_RULE_ADAPTER_REGISTRY;
